// ─────────────────────────────────────────────────────────────────────────────
// TrainerSettings.jsx — Phase 2 (P2-S6)
// Account settings dialog — displayed when trainer clicks their name chip.
// Allows the logged-in trainer to update their display name and password.
// No admin involvement needed — uses Supabase Auth updateUser() directly.
//
// Props:
//   open           — boolean, controls dialog visibility
//   onClose()      — called when user dismisses the dialog
//   currentName    — current display name (from userSession)
//   onNameUpdated  — called with (newName) when name is changed successfully
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Alert, Divider,
  CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { authService } from '../services/authService';

export default function TrainerSettings({ open, onClose, currentName, onNameUpdated }) {
  const [email,       setEmail]       = useState('');
  const [name,        setName]        = useState('');
  const [newPwd,      setNewPwd]      = useState('');
  const [confirmPwd,  setConfirmPwd]  = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [msg,         setMsg]         = useState(null); // { type: 'success'|'error', text }

  // Populate fields each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setName(currentName || '');
    setNewPwd('');
    setConfirmPwd('');
    setMsg(null);
    setEmail('');
    authService.getUser()
      .then(user => setEmail(user?.email || ''))
      .catch(() => {});
  }, [open, currentName]);

  // ── Update display name ──────────────────────────────────────────────────
  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      await authService.updateProfile({ fullName: name.trim() });
      onNameUpdated?.(name.trim());
      setMsg({ type: 'success', text: 'Display name updated.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Change password ──────────────────────────────────────────────────────
  const handleSavePassword = async () => {
    if (!newPwd) return;
    if (newPwd.length < 8) {
      setMsg({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }
    if (newPwd !== confirmPwd) {
      setMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await authService.updatePassword(newPwd);
      setNewPwd('');
      setConfirmPwd('');
      setMsg({ type: 'success', text: 'Password changed. Use the new password next time you log in.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Password change failed. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Account Settings</DialogTitle>
      <DialogContent>

        {msg && (
          <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}

        {/* Email — read-only, informational */}
        <TextField
          label="Email address" fullWidth size="small" sx={{ mb: 2 }}
          value={email} disabled
          helperText="Your login email cannot be changed here."
        />

        {/* Display name */}
        <TextField
          label="Display name" fullWidth size="small" sx={{ mb: 1.5 }}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSaveName()}
          placeholder="Dr. Tatar Radu"
        />
        <Button
          size="small" variant="outlined"
          onClick={handleSaveName}
          disabled={saving || !name.trim() || name.trim() === currentName}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ mb: 1 }}
        >
          Update Name
        </Button>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Change Password
        </Typography>
        <TextField
          label="New password" fullWidth size="small" sx={{ mb: 1.5 }}
          value={newPwd} onChange={e => setNewPwd(e.target.value)}
          type={showPwd ? 'text' : 'password'}
          helperText="Minimum 8 characters. Write it down — it cannot be recovered."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPwd(p => !p)}>
                  {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm new password" fullWidth size="small" sx={{ mb: 1.5 }}
          value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
          type={showPwd ? 'text' : 'password'}
        />

        {/* Security note */}
        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Passwords are stored as a one-way cryptographic hash. No one —
            including the administrator — can read or recover your password.
            If you forget it, a 24-hour delayed reset process is required.
          </Typography>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained" color="warning"
          onClick={handleSavePassword}
          disabled={saving || !newPwd || !confirmPwd}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Change Password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
